import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiTrash2, FiPlusCircle } from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import inventoryService from "../../services/inventoryService";
import Pagination from "../../components/common/Pagination";
import ConfirmModal from "../../components/common/ConfirmModal";

const TX_CONFIG = {
    "Stock In": { color: "#16a34a", bg: "#dcfce7" },
    "Stock Out": { color: "#dc2626", bg: "#fee2e2" },
    "Return": { color: "#003b67", bg: "#e8f1f8" },
    "Adjustment": { color: "#d97706", bg: "#fef3c7" },
};

const TX_TYPES = ["Stock In", "Stock Out", "Return", "Adjustment"];

export default function TransactionHistory() {
    const navigate = useNavigate();
    const { academicYear } = useApp();

    const [transactions, setTransactions] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loading, setLoading] = useState(true);

    const [typeFilter, setTypeFilter] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [page, setPage] = useState(1);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchTx = useCallback(async () => {
        setLoading(true);
        try {
            const res = await inventoryService.getTransactions({
                academicYear: academicYear?._id,
                type: typeFilter,
                fromDate,
                toDate,
                page,
                limit: 25,
            });
            setTransactions(res.data);
            setPagination(res.pagination);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, [academicYear?._id, typeFilter, fromDate, toDate, page]);

    useEffect(() => {
        fetchTx();
    }, [fetchTx]);

    useEffect(() => {
        setPage(1);
    }, [typeFilter, fromDate, toDate]);

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const res = await inventoryService.deleteTransaction(deleteTarget._id);
            toast.success(res.message);
            setDeleteTarget(null);
            fetchTx();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setDeleting(false);
        }
    };

    const clearFilters = () => {
        setTypeFilter("");
        setFromDate("");
        setToDate("");
    };
    const hasFilters = typeFilter || fromDate || toDate;

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">Transaction History</h1>
                    <p className="page-subtitle">{pagination?.total ?? 0} total transactions</p>
                </div>
                <button
                    className="btn btn-primary"
                    onClick={() => navigate("/inventory/transactions/new")}
                    style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}
                >
                    <FiPlusCircle size={14} /> New Transaction
                </button>
            </div>

            <div className="card">
                {/* Filters */}
                <div
                    style={{
                        padding: "0.75rem 1rem",
                        borderBottom: "1px solid var(--border)",
                        display: "flex",
                        gap: "0.65rem",
                        flexWrap: "wrap",
                        alignItems: "center",
                    }}
                >
                    <select
                        className="form-control"
                        style={{ width: 155, fontSize: 13 }}
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                    >
                        <option value="">All Types</option>
                        {TX_TYPES.map((t) => (
                            <option key={t} value={t}>
                                {t}
                            </option>
                        ))}
                    </select>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                        <input
                            type="date"
                            className="form-control"
                            style={{ width: 155, fontSize: 13 }}
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            placeholder="From date"
                        />
                        <span style={{ color: "var(--text-muted)", fontSize: 12 }}>to</span>
                        <input
                            type="date"
                            className="form-control"
                            style={{ width: 155, fontSize: 13 }}
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            placeholder="To date"
                        />
                    </div>
                    {hasFilters && (
                        <button className="btn btn-outline btn-sm" onClick={clearFilters}>
                            Clear
                        </button>
                    )}
                </div>

                {loading ? (
                    <div className="loading-center">
                        <div className="spinner" />
                        Loading transactions…
                    </div>
                ) : transactions.length === 0 ? (
                    <div className="empty-state">
                        <p>No transactions found.</p>
                    </div>
                ) : (
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Item</th>
                                    <th style={{ textAlign: "center" }}>Type</th>
                                    <th style={{ textAlign: "center" }}>Qty</th>
                                    <th style={{ textAlign: "center" }}>Before → After</th>
                                    <th>Details</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.map((tx) => {
                                    const tc = TX_CONFIG[tx.type] || TX_CONFIG["Adjustment"];
                                    const sign = tx.type === "Stock Out" ? "-" : "+";
                                    return (
                                        <tr key={tx._id}>
                                            <td
                                                style={{
                                                    fontSize: 12,
                                                    color: "var(--text-muted)",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {tx.transactionDate}
                                            </td>
                                            <td>
                                                <button
                                                    style={{
                                                        background: "none",
                                                        border: "none",
                                                        cursor: "pointer",
                                                        textAlign: "left",
                                                        padding: 0,
                                                    }}
                                                    onClick={() =>
                                                        navigate(`/inventory/items/${tx.item?._id}`)
                                                    }
                                                >
                                                    <div
                                                        style={{
                                                            fontWeight: 600,
                                                            fontSize: 13,
                                                            color: "#003b67",
                                                        }}
                                                    >
                                                        {tx.item?.name}
                                                    </div>
                                                </button>
                                                {tx.item?.itemCode && (
                                                    <div
                                                        style={{
                                                            fontSize: 11,
                                                            color: "var(--text-muted)",
                                                            fontFamily: "monospace",
                                                        }}
                                                    >
                                                        {tx.item.itemCode}
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ textAlign: "center" }}>
                                                <span
                                                    style={{
                                                        ...tc,
                                                        padding: "2px 8px",
                                                        borderRadius: 99,
                                                        fontSize: 11.5,
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {tx.type}
                                                </span>
                                            </td>
                                            <td
                                                style={{
                                                    textAlign: "center",
                                                    fontWeight: 700,
                                                    color: tc.color,
                                                    fontSize: 13,
                                                }}
                                            >
                                                {sign}
                                                {tx.quantity} {tx.item?.unit}
                                            </td>
                                            <td
                                                style={{
                                                    textAlign: "center",
                                                    fontSize: 12,
                                                    color: "var(--text-muted)",
                                                }}
                                            >
                                                {tx.stockBefore} →{" "}
                                                <strong style={{ color: "var(--text)" }}>
                                                    {tx.stockAfter}
                                                </strong>
                                            </td>
                                            <td style={{ fontSize: 12 }}>
                                                {tx.issuedToName && <div>{tx.issuedToName}</div>}
                                                {tx.supplier?.name && (
                                                    <div style={{ color: "var(--text-muted)" }}>
                                                        {tx.supplier.name}
                                                        {tx.invoiceNo ? ` · ${tx.invoiceNo}` : ""}
                                                    </div>
                                                )}
                                                {tx.adjustmentReason && (
                                                    <div style={{ color: "var(--text-muted)" }}>
                                                        {tx.adjustmentReason}
                                                    </div>
                                                )}
                                                {tx.remarks && (
                                                    <div
                                                        style={{
                                                            color: "var(--text-muted)",
                                                            fontStyle: "italic",
                                                        }}
                                                    >
                                                        {tx.remarks}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => setDeleteTarget(tx)}
                                                    title="Delete & reverse stock"
                                                >
                                                    <FiTrash2 size={11} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                <Pagination pagination={pagination} onPageChange={setPage} />
            </div>

            {deleteTarget && (
                <ConfirmModal
                    title="Delete Transaction"
                    message="Delete this transaction and reverse its stock change?"
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                    loading={deleting}
                />
            )}
        </div>
    );
}
